import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { graphql, PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay"
import { GroupedVirtuoso, GroupedVirtuosoHandle } from "react-virtuoso"
import { Button } from "@/components/ui/button"
import { appointmentsTable_waitingRoomDay$key } from "@/modules/waiting-room/components/appointment-table/__generated__/appointmentsTable_waitingRoomDay.graphql"
import { AppointmentEmpty } from "@/modules/waiting-room/components/appointment-table/appointment-empty"
import { AppointmentGroupHeaderContent } from "@/modules/waiting-room/components/appointment-table/appointment-group-header-content"
import { AppointmentItemContent } from "@/modules/waiting-room/components/appointment-table/appointment-item-content"
import { useAppointmentContext } from "@/modules/waiting-room/contexts/appointment-context"
import { useWaitingRoomFiltersContext } from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { useDrawerCloserWhenAppointmentNoLongerPresent } from "@/modules/waiting-room/hooks/useDrawerCloserWhenAppointmentNoLongerPresent"
import { useHandleSearchAppointment } from "@/modules/waiting-room/hooks/useHandleSearchAppointment"
import { WaitingRoomAppointmentsQuery } from "@/modules/waiting-room/services/__generated__/WaitingRoomAppointmentsQuery.graphql"
import { WaitingRoomDayGroup, waitingRoomQuery } from "@/modules/waiting-room/services/WaitingRoomAppointmentsQuery"

const APPOINTMENT_ROW_HEIGHT = 40

const AppointmentsTableFragment = graphql`
  fragment appointmentsTable_waitingRoomDay on WaitingRoomDay @relay(plural: true) {
    id
    date
    slots {
      id
      slotId
      timeChangeStatus
      appointments {
        id
        ...appointmentRowPatient_WaitingRoomAppointment
        ...appointmentRow_WaitingRoomAppointment
        ...appointmentSearch_WaitingRoomAppointment @relay(mask: false)
      }
    }
  }
`

interface AppointmentsTableProps {
  queryReference: PreloadedQuery<WaitingRoomAppointmentsQuery>
  hasFilters: boolean
}

const AppointmentsTable = ({ hasFilters, queryReference }: AppointmentsTableProps) => {
  const { t } = useTranslation()
  const selectedWaitingRoomAppointmentId = useAppointmentContext((state) => state.selectedWaitingRoomAppointmentId)
  const setShouldBeSelectedNextAppointmentId = useAppointmentContext(
    (state) => state.setShouldBeSelectedNextAppointmentId,
  )
  const setShouldBeSelectedPreviousAppointmentId = useAppointmentContext(
    (state) => state.setShouldBeSelectedPreviousAppointmentId,
  )

  const tableRef = useRef<GroupedVirtuosoHandle>(null)
  const setHasScrolledOverFirstUrgentAcuteAppointment = useAppointmentContext(
    (state) => state.setHasScrolledOverFirstUrgentAcuteAppointment,
  )
  const resetFilters = useWaitingRoomFiltersContext((state) => state.resetFilters)

  const data = usePreloadedQuery<WaitingRoomAppointmentsQuery>(waitingRoomQuery, queryReference)

  const waitingRoomAppointments = useFragment<appointmentsTable_waitingRoomDay$key>(
    AppointmentsTableFragment,
    data.waitingRoomAppointments.waitingRoomDays,
  )

  const filteredAppointments = useMemo(() => {
    return waitingRoomAppointments.reduce((acc: WaitingRoomDayGroup[], item) => {
      const filteredSlots = item.slots.filter((slot) => slot.appointments.length > 0)
      if (filteredSlots.length > 0) {
        acc.push({
          ...item,
          slots: filteredSlots,
        })
      }
      return acc
    }, [])
  }, [waitingRoomAppointments])

  const searchedAppointments = useHandleSearchAppointment(filteredAppointments)

  const hasAppointment = searchedAppointments.some((appointmentDate) =>
    appointmentDate.slots.some((slot) => slot.appointments.length),
  )

  const numberOfAppointmentsInGroup = searchedAppointments.map((item) =>
    item.slots.reduce((total, slot) => total + slot.appointments.length, 0),
  )

  const handleShadowInStickyHeader = (e: React.UIEvent<HTMLDivElement>, scrollTop: number) => {
    const firstElement = e.currentTarget.querySelector(":first-child")
    if (firstElement) {
      firstElement.classList.toggle("table-header-shadow", scrollTop > 0)
    }
  }

  useDrawerCloserWhenAppointmentNoLongerPresent(waitingRoomAppointments)

  useEffect(() => {
    const flatAppointmentIds = searchedAppointments
      .flatMap((x) => x.slots)
      .flatMap((x) => x.appointments)
      .map((x) => x.id)

    for (let index = 0; index < flatAppointmentIds.length; index++) {
      const id = flatAppointmentIds[index]
      if (id === selectedWaitingRoomAppointmentId) {
        setShouldBeSelectedNextAppointmentId(flatAppointmentIds[index + 1] ?? null)
        setShouldBeSelectedPreviousAppointmentId(flatAppointmentIds[index - 1] ?? null)
        break
      }
    }
  }, [
    searchedAppointments,
    selectedWaitingRoomAppointmentId,
    setShouldBeSelectedNextAppointmentId,
    setShouldBeSelectedPreviousAppointmentId,
  ])

  if (hasAppointment) {
    return (
      <GroupedVirtuoso
        onScroll={(event: React.UIEvent<HTMLDivElement>) => {
          const { scrollTop } = event.currentTarget
          if (scrollTop > APPOINTMENT_ROW_HEIGHT) setHasScrolledOverFirstUrgentAcuteAppointment(true)
          else setHasScrolledOverFirstUrgentAcuteAppointment(false)

          handleShadowInStickyHeader(event, scrollTop)
        }}
        ref={tableRef}
        className="virtualized-table mt-8 h-full overflow-x-hidden"
        groupCounts={numberOfAppointmentsInGroup}
        groupContent={(index) => (
          <AppointmentGroupHeaderContent
            index={index}
            numberOfAppointmentsInGroup={numberOfAppointmentsInGroup}
            appointmentGroups={searchedAppointments}
          />
        )}
        itemContent={(itemIndex, groupIndex) => (
          <AppointmentItemContent
            itemIndex={itemIndex}
            groupIndex={groupIndex}
            numberOfAppointmentsInGroup={numberOfAppointmentsInGroup}
            appointmentGroups={searchedAppointments}
          />
        )}
      />
    )
  }

  return (
    <AppointmentEmpty
      title={t("scheduling.waitingRoom.noFilterResult.title")}
      message={t("scheduling.waitingRoom.noFilterResult.message")}>
      {hasFilters && (
        <div className="mx-auto">
          <Button variant="secondary" onClick={resetFilters} data-testid="clear-filters-button">
            {t("common.button.clearFilters")}
          </Button>
        </div>
      )}
    </AppointmentEmpty>
  )
}

export { AppointmentsTable }
